import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from './Layout';
import StatusInfo from '../components/StatusInfo';
import { fmtMoney, fmtCount, plural } from '../utils/format';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const MONTHS_SHORT = ['', 'янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const MONTHS_FULL = ['', 'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
// N-089: money=true метрики графика — 2 знака после запятой (fmtMoney), остальные — целые (fmtCount)
const fmt = (n, money = false) => (money ? fmtMoney(n) : fmtCount(n));
const GRANULARITY_LABEL = { day: 'День', week: 'Неделя', month: 'Месяц' };

// N-047: метаданные переключаемых метрик графика (по аналогии с N-039 в админке)
const METRIC_META = {
  referred_all: { label: 'Направлено врачами', color: '#2EC4B6', money: true },
  total_to_pay: { label: 'Итого комиссия', color: '#149e90', money: true },
  referrals: { label: 'Направлений к вам', color: '#1B2B4B', money: false },
  patients_reached: { label: 'Дошли (оплатили)', color: '#10b981', money: false },
  transactions: { label: 'Транзакций', color: '#3b82f6', money: false },
};

function formatDMY(d) {
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}
function bucketDate(iso) { return new Date(iso + 'T00:00:00'); }
function bucketShortLabel(iso, granularity) {
  const d = bucketDate(iso);
  if (granularity === 'month') return MONTHS_SHORT[d.getMonth() + 1];
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function bucketFullLabel(iso, granularity) {
  const d = bucketDate(iso);
  if (granularity === 'month') return `${MONTHS_FULL[d.getMonth() + 1]} ${d.getFullYear()}`;
  if (granularity === 'week') {
    const end = new Date(d);
    end.setDate(end.getDate() + 6);
    return `${formatDMY(d)} – ${formatDMY(end)}`;
  }
  return formatDMY(d);
}

// YYYY-MM-DD для input[type=date]
function toInputDate(date) {
  return date.toISOString().split('T')[0];
}
function formatDateRu(str) {
  if (!str) return '';
  const [y, m, dd] = str.split('-');
  return `${dd}.${m}.${y}`;
}

const UPDATE_INFO = [['Обновление данных', 'Данные обновляются ежедневно в 03:00 (ночной опрос клиник).']];

const CARD_DESCS = {
  'Направлено врачами': 'Сумма, на которую пациенты, направленные врачами, оплатили услуги за выбранный период.',
  'Направлений к вам': 'Сколько пациентов врачи привязали к вам как к цели направления за выбранный период (по дате привязки).',
  'Дошли (оплатили)': 'Уникальные пациенты, у которых была хотя бы одна оплата за выбранный период.',
  'Транзакций': 'Количество дней с зафиксированной оплатой (одна запись = одна суммарная оплата пациента за день).',
  'Комиссия врачам': 'Комиссии, которые Refero должна перечислить врачам за направленных к вам пациентов.',
  'Комиссия Refero': 'Наш процент от суммы оплат — не входит в выплату врачам, это отдельно ваш платёж в адрес Refero.',
  'Итого комиссия': 'Сумма к выплате врачам + комиссия Refero — именно столько нужно оплатить Refero за период.',
};

// N-115: метрики операционной вкладки — намеренно за всё время, без описания в CARD_DESCS
// стандартной вкладки (свой словарь, т.к. смысл другой — не завязан на период фильтра).
const OP_CARD_DESCS = {
  'Направлений на операции': 'Сколько раз врачи направляли пациентов к вам по программе «Операции» за всё время.',
  'Дошли (оплатили)': 'Направления, по которым пациент хотя бы раз оплатил в течение установленного вами периода — по ним автоматически выставлен счёт.',
  'Комиссия врачам': 'Сумма комиссий врачам по всем закрытым операциям за всё время (независимо от статуса оплаты счёта).',
  'Комиссия Refero': 'Наш процент от суммы операций за всё время — отдельно от выплаты врачам, это ваш платёж в адрес Refero.',
  'Итого комиссия': 'Комиссия врачам + комиссия Refero по всем операциям за всё время.',
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('standard'); // N-115: 'standard' | 'operations'
  const [d, setD] = useState(null);
  const [opD, setOpD] = useState(null);
  const [mode, setMode] = useState('all'); // 'all' | 'range'
  const [dateFrom, setDateFrom] = useState(toInputDate(new Date(Date.now() - 30 * 86400000)));
  const [dateTo, setDateTo] = useState(toInputDate(new Date()));
  const [applied, setApplied] = useState(null); // { from, to } — применённый период
  const [showConditionsInfo, setShowConditionsInfo] = useState(false);
  const [activeMetric, setActiveMetric] = useState('referred_all'); // N-047: метрика на общем графике
  const [granularity, setGranularity] = useState('month'); // 'day' | 'week' | 'month'
  const [series, setSeries] = useState([]);
  const headers = { Authorization: 'Bearer ' + localStorage.getItem('clinicToken') };

  const load = useCallback(() => {
    const params = applied ? { date_from: applied.from, date_to: applied.to } : {};
    axios.get(`${API}/clinic-cabinet/dashboard`, { headers, params })
      .then(r => setD(r.data))
      .catch(() => { localStorage.clear(); window.location.href = '/login'; });
    // eslint-disable-next-line
  }, [applied]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const params = { metric: activeMetric, granularity };
    if (applied) { params.date_from = applied.from; params.date_to = applied.to; }
    axios.get(`${API}/clinic-cabinet/dashboard/series`, { headers, params }).then(r => setSeries(r.data)).catch(() => setSeries([]));
    // eslint-disable-next-line
  }, [activeMetric, granularity, applied]);

  useEffect(() => {
    axios.get(`${API}/clinic-cabinet/dashboard/operations`, { headers }).then(r => setOpD(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const applyPeriod = () => {
    if (!dateFrom || !dateTo) return;
    setApplied({ from: dateFrom, to: dateTo });
    setMode('range');
  };
  const resetPeriod = () => { setApplied(null); setMode('all'); };

  if (!d) return <Layout><div style={s.muted}>Загрузка…</div></Layout>;

  const hasPeriod = mode === 'range' && applied;
  const periodLabel = hasPeriod ? `${formatDateRu(applied.from)} — ${formatDateRu(applied.to)}` : 'за всё время';

  const cards = [
    { label: 'Направлено врачами', value: `${fmtMoney(d.total_referred_amount)} ₽`, color: '#2EC4B6', big: true, href: '/referrals', metric: 'referred_all' },
    { label: 'Направлений к вам', value: d.referrals_count, color: '#1B2B4B', href: '/referrals', metric: 'referrals' },
    { label: 'Дошли (оплатили)', value: d.patients_reached, color: '#2EC4B6', href: '/referrals', metric: 'patients_reached' },
    { label: 'Транзакций', value: d.transactions_count, color: '#1B2B4B', metric: 'transactions' },
    { label: 'Комиссия врачам', value: `${fmtMoney(d.total_owed_doctors)} ₽`, color: '#1B2B4B', wide: true, href: '/commissions' },
    { label: 'Комиссия Refero', value: `${fmtMoney(d.total_refero_fee)} ₽`, color: '#f59e0b', wide: true, href: '/commissions' },
    { label: 'Итого комиссия', value: `${fmtMoney(d.total_to_pay)} ₽`, color: '#2EC4B6', big: true, href: '/commissions', metric: 'total_to_pay' },
  ];

  return (
    <Layout>
      <h1 style={s.h1}>Обзор<StatusInfo items={UPDATE_INFO} /></h1>
      {d.unpaid_invoices_count > 0 && (
        <div style={s.unpaidBanner} onClick={() => navigate('/commissions?tab=months')}>
          <span>
            У вас {d.unpaid_invoices_count === 1 ? 'есть выставленный счёт' : `есть ${d.unpaid_invoices_count} выставленных ${plural(d.unpaid_invoices_count, ['счёт', 'счёта', 'счетов'])}`} на сумму{' '}
            <b>{fmtMoney(d.unpaid_invoices_total)} ₽</b> — пожалуйста, оплатите {d.unpaid_invoices_count === 1 ? 'его' : 'их'}.
          </span>
          <span style={s.unpaidArrow}>Перейти к оплате →</span>
        </div>
      )}
      <div style={s.tabs}>
        <button style={{ ...s.tabBtn, ...(tab === 'standard' ? s.tabBtnActive : {}) }} onClick={() => setTab('standard')}>Стандартные направления</button>
        <button style={{ ...s.tabBtn, ...(tab === 'operations' ? s.tabBtnActive : {}) }} onClick={() => setTab('operations')}>Операционные направления</button>
      </div>

      {tab === 'standard' && (
        <>
          {d.conditions && (
            <div style={s.cond}>
              <span>
                Условия: первичная <b>{d.conditions.commission_primary_pct}%</b> · повторная <b>{d.conditions.commission_repeat_pct}%</b> · квота <b>{d.conditions.monthly_quota}</b>/мес · таймер <b>{d.conditions.timeout_days}</b> дн.
              </span>
              <button style={s.btnConditions} onClick={() => setShowConditionsInfo(true)}>Изменить условия ⓘ</button>
            </div>
          )}
          <div style={s.filterBar}>
            <button style={{ ...s.btnGhost, ...(mode === 'all' ? s.btnGhostActive : {}) }} onClick={resetPeriod}>Всё время</button>
            <input type="date" style={s.dateInput} value={dateFrom} max={dateTo} onChange={e => setDateFrom(e.target.value)} />
            <span style={s.muted}>—</span>
            <input type="date" style={s.dateInput} value={dateTo} min={dateFrom} max={toInputDate(new Date())} onChange={e => setDateTo(e.target.value)} />
            <button style={s.btn} onClick={applyPeriod}>Применить</button>
          </div>
          {hasPeriod && <div style={s.hint}>Все метрики ниже — за {periodLabel}.</div>}
          <div style={s.grid}>
            {cards.map(c => {
              const isActiveChart = c.metric === activeMetric;
              return (
                <div key={c.label}
                  style={{ ...s.card, ...(c.big ? s.cardBig : c.wide ? s.cardWide : {}), ...(c.href ? s.cardClickable : {}) }}
                  onClick={c.href ? () => navigate(c.href) : undefined}>
                  <div style={s.cardLabel}>
                    {c.label}
                    {c.href && <span style={s.cardArrow}>→</span>}
                    <span onClick={e => e.stopPropagation()}>
                      <StatusInfo items={[[c.label, CARD_DESCS[c.label]]]} />
                    </span>
                  </div>
                  <div style={{ ...s.cardValue, color: c.color }}>{c.value}</div>
                  {c.metric && (
                    <button
                      title={`Показать «${c.label}» на графике`}
                      style={{ ...s.chartIconBtn, ...(isActiveChart ? s.chartIconBtnActive : {}) }}
                      onClick={e => { e.preventDefault(); e.stopPropagation(); setActiveMetric(c.metric); }}
                    >
                      <ChartIcon active={isActiveChart} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <div style={s.chartCard}>
            <div style={s.chartHeader}>
              <div style={s.cardTitle}>{METRIC_META[activeMetric].label} — по {(
                { day: 'дням', week: 'неделям', month: 'месяцам' }
              )[granularity]}</div>
              <div style={s.granGroup}>
                {['day', 'week', 'month'].map(g => (
                  <button
                    key={g}
                    style={{ ...s.granBtn, ...(granularity === g ? s.granBtnActive : {}) }}
                    onClick={() => setGranularity(g)}
                  >
                    {GRANULARITY_LABEL[g]}
                  </button>
                ))}
              </div>
            </div>
            <AreaChart series={series} color={METRIC_META[activeMetric].color} isMoney={METRIC_META[activeMetric].money} granularity={granularity} />
          </div>
        </>
      )}

      {tab === 'operations' && (
        <>
          {!opD ? <div style={s.muted}>Загрузка…</div> : (
            <>
              {opD.conditions ? (
                <div style={s.cond}>
                  <span>
                    Условия «Операций»: комиссия <b>{opD.conditions.commission_pct}%</b> от суммы всех оплат пациента · период <b>{opD.conditions.timeout_days}</b> дн. с момента направления
                  </span>
                  <button style={s.btnConditions} onClick={() => setShowConditionsInfo(true)}>Изменить условия ⓘ</button>
                </div>
              ) : (
                <div style={s.cond}>
                  <span>Условия «Операций» не заданы — программа не видна врачам.</span>
                  <button style={s.btnConditions} onClick={() => setShowConditionsInfo(true)}>Изменить условия ⓘ</button>
                </div>
              )}
              <div style={s.hint}>Метрики ниже — за всё время (программа «Операции» не привязана к календарному месяцу).</div>
              <div style={s.grid}>
                {[
                  { label: 'Направлений на операции', value: opD.referrals_count, color: '#1B2B4B' },
                  { label: 'Дошли (оплатили)', value: opD.reached_count, color: '#2EC4B6' },
                  { label: 'Комиссия врачам', value: `${fmtMoney(opD.doctor_amount_total)} ₽`, color: '#1B2B4B', wide: true, href: '/operations' },
                  { label: 'Комиссия Refero', value: `${fmtMoney(opD.platform_fee_total)} ₽`, color: '#f59e0b', wide: true, href: '/operations' },
                  { label: 'Итого комиссия', value: `${fmtMoney(opD.total_commission)} ₽`, color: '#2EC4B6', big: true, href: '/operations' },
                ].map(c => (
                  <div key={c.label}
                    style={{ ...s.card, ...(c.big ? s.cardBig : c.wide ? s.cardWide : {}), ...(c.href ? s.cardClickable : {}) }}
                    onClick={c.href ? () => navigate(c.href) : undefined}>
                    <div style={s.cardLabel}>
                      {c.label}
                      {c.href && <span style={s.cardArrow}>→</span>}
                      <span onClick={e => e.stopPropagation()}>
                        <StatusInfo items={[[c.label, OP_CARD_DESCS[c.label]]]} />
                      </span>
                    </div>
                    <div style={{ ...s.cardValue, color: c.color }}>{c.value}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {showConditionsInfo && (
        <div style={s.modalOverlay} onClick={() => setShowConditionsInfo(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>Изменение условий</h3>
            <p style={s.modalText}>
              {tab === 'operations'
                ? 'Ставка комиссии и период программы «Операции» задаются и меняются только через техподдержку Refero — самостоятельно изменить их в личном кабинете нельзя. Если нужно пересмотреть условия, свяжитесь с поддержкой.'
                : 'Ставки комиссии и квота задаются и меняются только через техподдержку Refero — самостоятельно изменить их в личном кабинете нельзя. Если нужно пересмотреть условия, свяжитесь с поддержкой.'}
            </p>
            <div style={s.modalActions}>
              <button style={s.btnGhostModal} onClick={() => setShowConditionsInfo(false)}>Закрыть</button>
              <button style={s.btnPrimaryModal} onClick={() => navigate('/support')}>Связаться с техподдержкой</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function ChartIcon({ active }) {
  const c = active ? '#2EC4B6' : '#94a3b8';
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M4 20V10M12 20V4M20 20V14" stroke={c} strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

// ── Лёгкий area-чарт на SVG, без зависимостей, с тултипом при наведении ──
// N-047: обобщён под произвольную метрику/гранулярность (по аналогии с админкой N-039)
function AreaChart({ series, color = '#2EC4B6', isMoney = false, granularity = 'month' }) {
  const [hover, setHover] = useState(null);
  const W = 760, H = 240, padL = 48, padR = 16, padT = 16, padB = 28;
  const max = Math.max(1, ...series.map(p => p.value));
  const niceMax = isMoney ? (Math.ceil(max / 2000) * 2000 || 2000) : (Math.ceil(max / 5) * 5 || 5);
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const x = (i) => padL + (series.length === 1 ? plotW / 2 : (i / (series.length - 1)) * plotW);
  const y = (v) => padT + plotH - (v / niceMax) * plotH;

  const pts = series.map((p, i) => `${x(i)},${y(p.value)}`);
  const linePath = 'M' + pts.join(' L');
  const areaPath = series.length ? `${linePath} L${x(series.length - 1)},${padT + plotH} L${x(0)},${padT + plotH} Z` : '';
  const ticks = [0, niceMax / 4, niceMax / 2, (niceMax * 3) / 4, niceMax];
  const hp = hover != null ? series[hover] : null;
  const labelStep = Math.max(1, Math.ceil(series.length / 12));
  const tickLabel = (t) => (t === 0 ? '0' : isMoney ? `${(t / 1000)}т` : `${Math.round(t)}`);
  const valueLabel = (v) => (isMoney ? `${fmt(v, true)} ₽` : `${fmt(v)}`);

  return (
    <div style={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        <defs>
          <linearGradient id="clinicAreaFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={padL} y1={y(t)} x2={W - padR} y2={y(t)} stroke="#EEF2F7" strokeWidth="1" />
            <text x={padL - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill="#94A3B8">{tickLabel(t)}</text>
          </g>
        ))}
        <path d={areaPath} fill="url(#clinicAreaFill)" />
        <path d={linePath} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        {hp && <line x1={x(hover)} y1={padT} x2={x(hover)} y2={padT + plotH} stroke={color} strokeWidth="1" strokeDasharray="4 4" opacity="0.5" />}
        {series.map((p, i) => (
          <circle key={i} cx={x(i)} cy={y(p.value)} r={hover === i ? 5 : 3} fill={hover === i ? color : '#fff'} stroke={color} strokeWidth="2" />
        ))}
        {series.map((p, i) => (
          (i % labelStep === 0 || i === series.length - 1) && (
            <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize="11" fill={hover === i ? '#149e90' : '#94A3B8'} fontWeight={hover === i ? 700 : 400}>
              {bucketShortLabel(p.bucket, granularity)}
            </text>
          )
        ))}
        {series.map((p, i) => (
          <rect key={i} x={x(i) - plotW / series.length / 2} y={padT} width={plotW / series.length} height={plotH}
            fill="transparent" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} style={{ cursor: 'pointer' }} />
        ))}
      </svg>
      {hp && (
        <div style={{
          position: 'absolute', left: `${(x(hover) / W) * 100}%`, top: `${(y(hp.value) / H) * 100}%`,
          transform: 'translate(-50%, -125%)', pointerEvents: 'none',
          background: '#1B2B4B', color: '#fff', borderRadius: 10, padding: '8px 12px', whiteSpace: 'nowrap',
          boxShadow: '0 6px 20px rgba(27,43,75,0.25)', zIndex: 2,
        }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginBottom: 2 }}>{bucketFullLabel(hp.bucket, granularity)}</div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{valueLabel(hp.value)}</div>
        </div>
      )}
    </div>
  );
}

const s = {
  h1: { color: '#1B2B4B', fontSize: 24, fontWeight: 700, margin: '0 0 8px' },
  tabs: { display: 'flex', gap: 8, marginBottom: 16 },
  tabBtn: { background: '#fff', color: '#64748b', border: '1px solid #d7dee8', borderRadius: 10, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  tabBtnActive: { background: '#1B2B4B', color: '#fff', borderColor: '#1B2B4B' },
  cond: { color: '#475569', fontSize: 14, background: '#fff', border: '1px solid #eef2f7', borderRadius: 10, padding: '10px 16px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  unpaidBanner: { color: '#9a3412', fontSize: 14, background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', cursor: 'pointer' },
  unpaidArrow: { color: '#c2410c', fontWeight: 600, whiteSpace: 'nowrap' },
  btnConditions: { background: '#fff', color: '#b45309', border: '1px solid #fcd34d', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  filterBar: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 },
  dateInput: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 8, padding: '7px 10px', fontSize: 13, outline: 'none' },
  btn: { background: '#2EC4B6', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { background: '#fff', color: '#475569', border: '1px solid #d7dee8', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer' },
  btnGhostActive: { background: '#e9faf7', color: '#149e90', border: '1px solid #2EC4B6', fontWeight: 600 },
  hint: { fontSize: 12, color: '#94a3b8', background: '#fff', borderRadius: 8, padding: '8px 14px', marginBottom: 20, borderLeft: '3px solid #eef2f7' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 },
  card: { background: '#fff', borderRadius: 16, padding: 22, boxShadow: '0 4px 16px rgba(27,43,75,0.05)', position: 'relative' },
  cardClickable: { cursor: 'pointer' },
  cardArrow: { marginLeft: 6, color: '#94a3b8' },
  cardWide: { gridColumn: 'span 2' },
  cardBig: { gridColumn: 'span 2', background: '#e9faf7' },
  cardLabel: { color: '#64748b', fontSize: 13, marginBottom: 10, display: 'flex', alignItems: 'center' },
  cardValue: { fontSize: 28, fontWeight: 700 },
  chartIconBtn: { position: 'absolute', right: 14, bottom: 14, background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6, display: 'flex' },
  chartIconBtnActive: { background: 'rgba(46,196,182,0.15)' },
  chartCard: { background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 4px 16px rgba(27,43,75,0.05)', marginTop: 16 },
  chartHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 },
  cardTitle: { fontSize: 17, fontWeight: 700, color: '#1B2B4B' },
  granGroup: { display: 'flex', gap: 4, background: '#F6F8FB', borderRadius: 8, padding: 3 },
  granBtn: { background: 'transparent', border: 'none', color: '#64748b', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer' },
  granBtnActive: { background: '#fff', color: '#1B2B4B', fontWeight: 600, boxShadow: '0 1px 3px rgba(27,43,75,0.1)' },
  note: { color: '#94a3b8', fontSize: 12, marginTop: 16 },
  muted: { color: '#64748b' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(27,43,75,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 16, padding: 28, width: 440, maxWidth: '90vw', boxShadow: '0 20px 50px rgba(27,43,75,0.2)' },
  modalTitle: { color: '#1B2B4B', fontSize: 18, fontWeight: 700, margin: '0 0 12px' },
  modalText: { color: '#475569', fontSize: 14, lineHeight: 1.5, margin: '0 0 20px' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 10 },
  btnGhostModal: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 10, padding: '10px 18px', fontSize: 14, cursor: 'pointer' },
  btnPrimaryModal: { background: '#2EC4B6', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer', textDecoration: 'none', display: 'inline-block' },
};
